import { Skeleton } from 'components/common';
import { ViewModal } from 'components/core';
import { Key, useContext, useState } from 'react';
import { ALL_FILTERS } from 'utils/constants/Filters';
import { ColumnContext, FilterContext, ViewContext } from 'utils/context';
import { ModalVariant } from 'utils/types/saved-view';

import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  Flex,
  Icon,
  IconButton,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';

interface IViewButton {
  doSearch: () => void;
}

function ViewButton({ doSearch }: IViewButton) {
  const { t } = useTranslation();
  const [variant, setVariant] = useState<ModalVariant | ''>('');
  const { hasFilters } = useContext(FilterContext);
  const {
    canUpdate,
    selectedView,
    viewType,
    savedOrders,
    savedShipments,
    getViewRequest,
    revertView,
  } = useContext(ViewContext);
  const { hasColumnsModified } = useContext(ColumnContext);

  const { createView, isLoading, updateView } =
    viewType === 'order' ? savedOrders : savedShipments;

  const hasFiltersApplied = hasFilters(ALL_FILTERS);

  const saveButtonVisible = hasFiltersApplied || hasColumnsModified;
  const updateButtonDisabled = !hasFiltersApplied || !canUpdate;

  const isSavedView = !['all-shipments', 'all-orders'].includes(
    selectedView?.id || '',
  );

  const getDisabledKeys = () => {
    const disabledKeys = [];

    if (updateButtonDisabled) {
      disabledKeys.push('create');
    }

    if (!canUpdate) {
      disabledKeys.push('reset');
    }

    return disabledKeys;
  };

  const handleChangeAction = (key: Key) => {
    setVariant(String(key) as ModalVariant);
  };

  const actionOptions = [
    { id: 'create', label: t('core.createAsNewView') },
    { id: 'reset', label: t('core.resetView') },
  ];

  const disabledKeys = getDisabledKeys();
  const allActionsDisabled = disabledKeys.length === actionOptions.length;

  const getContent = () => {
    if (isLoading) {
      return (
        <Skeleton
          variant="dark"
          sx={{ width: '160px', height: '40px', borderRadius: '2px 0 0 2px' }}
        />
      );
    }

    if (isSavedView) {
      return (
        <Flex align="center">
          <Button
            variant="secondary"
            isDisabled={updateButtonDisabled}
            onPress={() => {
              setVariant('update');
            }}
          >
            {t('core.saveView')}
          </Button>
          <Dropdown>
            <IconButton
              data-testid="trigger"
              css={{
                backgroundColor: 'white',
                color: '$palette-grey-900',
                borderRadius: '0 2px 2px 0',
                paddingX: 1,
              }}
            >
              <Icon icon="expand_more" />
            </IconButton>
            <DropdownMenu
              disabledKeys={disabledKeys}
              onAction={handleChangeAction}
            >
              {actionOptions.map(({ id, label }) => (
                <DropdownItem key={id}>{label}</DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </Flex>
      );
    }

    if (saveButtonVisible) {
      return (
        <Button
          variant="secondary"
          onPress={() => {
            setVariant('create');
          }}
        >
          {t('core.saveViewAs')}
        </Button>
      );
    }
  };

  const buttonContent = getContent();

  const visibleButton = isSavedView
    ? !(allActionsDisabled && updateButtonDisabled)
    : saveButtonVisible;

  return visibleButton ? (
    <>
      {buttonContent}
      <ViewModal
        createView={createView}
        doSearch={doSearch}
        getViewRequest={getViewRequest}
        loading={isLoading}
        onClose={() => {
          setVariant('');
        }}
        revertView={revertView}
        selectedView={selectedView}
        updateView={updateView}
        variant={variant as ModalVariant}
        viewType={viewType}
      />
    </>
  ) : null;
}

export default ViewButton;
